import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AISHA_API_BASE } from '../constants';

/**
 * マグカップ デザイン（配置）画面。
 * MyGarageが iframe で開く想定:
 *   無地: /design?timeline_id=xxx&user_id=200120&variant_id=1320&embed=1
 *   色付: /design?timeline_id=xxx&user_id=200120&goods_id=76&variant_id=11051&embed=1
 *   （goods_id 省略=無地139。色付は goods_id=76 + 色のvariant_id）
 * - 印刷エリア(マグ展開図)に生成画像を配置（スワイプ移動・四隅リサイズ・中心ガイド）
 * - 「プレビュー更新」で prepare(position) を叩いてモックアップ表示
 * - 「確定」で親(MyGarage)へ postMessage（position + mockup_url + 金額）
 */

interface PrintArea {
  area_width: number;
  area_height: number;
  default_position: { top: number; left: number; width: number; height: number };
}
interface Pos { top: number; left: number; width: number; height: number; }

/** 画像の縦横比を維持して、既定の印刷ボックス内にフィット（中央寄せ）した初期配置を返す */
function fitPosition(area: PrintArea, aspect: number): Pos {
  const dp = area.default_position;
  let w = dp.width, h = w / aspect;
  if (h > dp.height) { h = dp.height; w = h * aspect; }
  return {
    width: Math.round(w),
    height: Math.round(h),
    left: Math.round(dp.left + (dp.width - w) / 2),
    top: Math.round(dp.top + (dp.height - h) / 2),
  };
}

/** 下部のマグ目印（空のワイヤーフレーム）。位置の目安だけを示す（Printful同様、絵は入れない） */
const MugMarker: React.FC<{ side: 'left' | 'right' }> = ({ side }) => {
  const stroke = '#9aa0a6';
  return (
    <svg width={66} height={52} viewBox="0 0 66 52" fill="none">
      {side === 'left' ? (
        <>
          <path d="M 16 16 q -11 0 -11 12 q 0 12 11 12" stroke={stroke} strokeWidth={1.6} />
          <rect x={14} y={8} width={44} height={40} rx={9} stroke={stroke} strokeWidth={1.6} />
        </>
      ) : (
        <>
          <rect x={8} y={8} width={44} height={40} rx={9} stroke={stroke} strokeWidth={1.6} />
          <path d="M 52 16 q 11 0 11 12 q 0 12 -11 12" stroke={stroke} strokeWidth={1.6} />
        </>
      )}
    </svg>
  );
};

const MugDesignView: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const timelineId = params.get('timeline_id') || '';
  const userId = params.get('user_id') || '';
  const variantId = params.get('variant_id') || '1320';
  // 無地(139)/色付(76)の切替。MyGarageがiframe URLで渡す。未指定は無地。
  const goodsId = params.get('goods_id') || params.get('goods_management_id') || '';

  const [imageUrl, setImageUrl] = useState('');
  const [printArea, setPrintArea] = useState<PrintArea | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  const [imgAspect, setImgAspect] = useState<number | null>(null);
  const [priceInfo, setPriceInfo] = useState<{ total: number | null; currency: string }>({ total: null, currency: 'JPY' });
  const [mockups, setMockups] = useState<string[]>([]);
  const [selectedMockup, setSelectedMockup] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');

  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasW, setCanvasW] = useState(640);
  const dragRef = useRef<{ mode: 'move' | 'resize'; startX: number; startY: number; startPos: Pos } | null>(null);

  // 印刷エリア単位 → 画面px のスケール
  const scale = printArea ? canvasW / printArea.area_width : 1;
  const canvasH = printArea ? printArea.area_height * scale : 0;

  // 初期ロード: prepare(skip_mockup) で 画像URL・印刷エリア・価格を取得
  useEffect(() => {
    if (!timelineId) { setError('timeline_id がありません'); setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${AISHA_API_BASE}/external/printful/mug/prepare/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ timeline_id: timelineId, variant_id: Number(variantId), skip_mockup: true, ...(goodsId ? { goods_id: Number(goodsId) } : {}) }),
        });
        const data = await res.json();
        if (!data.success) { setError(data.error || '読み込みに失敗しました'); setLoading(false); return; }
        setImageUrl(data.image_url);
        setPrintArea(data.print_area);
        // pos は画像の縦横比が分かってから fitPosition で初期化（伸び防止）
        setPriceInfo({ total: data.total ?? null, currency: data.currency || 'JPY' });
      } catch (e: any) {
        setError(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [timelineId, variantId]);

  // 画像の縦横比（リサイズ時のアスペクト維持用）
  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.onload = () => { if (img.naturalHeight) setImgAspect(img.naturalWidth / img.naturalHeight); };
    img.src = imageUrl;
  }, [imageUrl]);

  // 印刷エリアと画像の縦横比が揃ったら、伸ばさずフィットした初期配置をセット
  useEffect(() => {
    if (printArea && imgAspect && !pos) setPos(fitPosition(printArea, imgAspect));
  }, [printArea, imgAspect, pos]);

  // キャンバス幅を実測（レスポンシブ）
  useEffect(() => {
    const measure = () => {
      const w = canvasRef.current?.clientWidth;
      if (w) setCanvasW(w);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [printArea]);

  // ドラッグ（移動 / リサイズ）。pointer events でタッチ・マウス両対応
  const startDrag = useCallback((mode: 'move' | 'resize', e: React.PointerEvent) => {
    if (!pos) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX, startY = e.clientY, startPos = pos;
    const onMove = (ev: PointerEvent) => {
      const dxArea = (ev.clientX - startX) / scale;
      const dyArea = (ev.clientY - startY) / scale;
      if (mode === 'move') {
        setPos({ ...startPos, left: Math.round(startPos.left + dxArea), top: Math.round(startPos.top + dyArea) });
      } else {
        const w = Math.max(60, Math.round(startPos.width + dxArea));
        setPos({ ...startPos, width: w, height: Math.round(w / (imgAspect || 1)) });
      }
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [pos, scale, imgAspect]);

  // プレビュー更新: prepare(position) でモックアップ生成（~50秒）
  const updatePreview = async () => {
    if (!pos) return;
    setPreviewing(true);
    setError('');
    try {
      const res = await fetch(`${AISHA_API_BASE}/external/printful/mug/prepare/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timeline_id: timelineId, variant_id: Number(variantId), position: pos, ...(goodsId ? { goods_id: Number(goodsId) } : {}) }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'プレビュー生成に失敗しました'); return; }
      const list: string[] = (data.mockups && data.mockups.length) ? data.mockups : (data.generated_mug_image ? [data.generated_mug_image] : []);
      setMockups(list);
      if (list.length) setSelectedMockup(list[0]);
      if (data.total != null) setPriceInfo({ total: data.total, currency: data.currency || 'JPY' });
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setPreviewing(false);
    }
  };

  // 確定: 親(MyGarage)へ postMessage
  const confirmDesign = () => {
    window.parent?.postMessage({
      type: 'MUG_DESIGN_CONFIRMED',
      timeline_id: timelineId,
      user_id: userId,
      variant_id: Number(variantId),
      goods_id: goodsId ? Number(goodsId) : undefined,
      position: pos,
      mockup_url: selectedMockup || mockups[0] || '',
      mockups: mockups,
      total: priceInfo.total,
      currency: priceInfo.currency,
    }, '*');
  };

  const resetPosition = () => {
    if (printArea && imgAspect) setPos(fitPosition(printArea, imgAspect));
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: '#666' }}>読み込み中…</div>;
  }
  if (error && !printArea) {
    return <div style={{ padding: 24, color: '#c00' }}>エラー: {error}</div>;
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 16, fontFamily: 'sans-serif', userSelect: 'none' }}>
      {/* ツールバー */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', borderBottom: '1px solid #eee', marginBottom: 16 }}>
        <strong style={{ color: '#222' }}>位置</strong>
        <button onClick={resetPosition} style={btnGhost}>リセット</button>
        <span style={{ marginLeft: 'auto', color: '#888', fontSize: 12 }}>画像をスワイプで移動・右下で拡大縮小</span>
      </div>

      {/* キャンバス（印刷エリア = マグ展開図） */}
      <div
        ref={canvasRef}
        style={{
          position: 'relative',
          width: '100%',
          height: canvasH,
          border: '1px dashed #bbb',
          background: '#fafafa',
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {/* ガイド線（左・中央・右） */}
        {[20, 50, 80].map((p) => (
          <div key={p} style={{ position: 'absolute', left: `${p}%`, top: 0, bottom: 0, width: 1, background: p === 50 ? '#777' : '#bbb', opacity: 0.6 }} />
        ))}
        {/* 配置画像 */}
        {pos && imageUrl && (
          <div
            onPointerDown={(e) => startDrag('move', e)}
            style={{
              position: 'absolute',
              left: pos.left * scale,
              top: pos.top * scale,
              width: pos.width * scale,
              height: pos.height * scale,
              cursor: 'move',
              boxShadow: '0 0 0 1.5px #29b6f6',
              touchAction: 'none',
            }}
          >
            <img src={imageUrl} alt="design" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none' }} />
            {/* 右下リサイズハンドル */}
            <div
              onPointerDown={(e) => startDrag('resize', e)}
              style={{
                position: 'absolute', right: -8, bottom: -8, width: 16, height: 16,
                background: '#29b6f6', borderRadius: '50%', border: '2px solid #fff',
                cursor: 'nwse-resize', touchAction: 'none',
              }}
            />
          </div>
        )}
      </div>
      {/* 下部: マグの目印（空・位置の目安）＋ガイド線の続き */}
      {printArea && (
        <div style={{ position: 'relative', height: 76, marginTop: 0 }}>
          {/* ガイド線の続き（キャンバスから下へ） */}
          {[20, 50, 80].map((p) => (
            <div key={p} style={{ position: 'absolute', left: `${p}%`, top: 0, height: 30, width: 1, background: p === 50 ? '#777' : '#bbb', opacity: 0.6 }} />
          ))}
          <div style={{ position: 'absolute', left: '20%', top: 26, transform: 'translateX(-50%)' }}><MugMarker side="left" /></div>
          <div style={{ position: 'absolute', left: '50%', top: 34, transform: 'translateX(-50%)', color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>マグカップの中心</div>
          <div style={{ position: 'absolute', left: '80%', top: 26, transform: 'translateX(-50%)' }}><MugMarker side="right" /></div>
        </div>
      )}

      {/* アクション */}
      <div style={{ marginTop: 20 }}>
        {/* プレビュー作成/更新 */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={updatePreview} disabled={previewing} style={previewing ? btnDisabled : btnPrimary}>
            {previewing ? 'プレビュー生成中…（最大1分）' : (mockups.length ? 'プレビューを更新' : 'プレビューを作成')}
          </button>
          {priceInfo.total != null && (
            <span style={{ marginLeft: 'auto', alignSelf: 'center', fontWeight: 700 }}>
              ¥{priceInfo.total.toLocaleString()}（{priceInfo.currency}）
            </span>
          )}
        </div>

        {/* 確定（プレビュー作成後のみ表示・目立たせる） */}
        {mockups.length > 0 && !previewing && (
          <div style={{ marginTop: 16 }}>
            <div style={{ color: '#16a34a', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              ✓ プレビューができました。内容でよければ下のボタンで確定してください
            </div>
            <button onClick={confirmDesign} style={btnConfirm}>
              このデザインで確定する →
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ color: '#c00', marginTop: 12 }}>{error}</div>}

      {/* モックアップ ギャラリー（全アングル） */}
      {mockups.length > 0 && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>完成イメージ（{mockups.length}枚）</div>
          <img
            src={selectedMockup || mockups[0]}
            alt="mockup"
            style={{ maxWidth: '100%', maxHeight: 380, borderRadius: 8, border: '1px solid #eee' }}
          />
          {mockups.length > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
              {mockups.map((u) => (
                <img
                  key={u}
                  src={u}
                  alt="thumb"
                  onClick={() => setSelectedMockup(u)}
                  style={{
                    width: 72, height: 72, objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                    border: (selectedMockup || mockups[0]) === u ? '2px solid #29b6f6' : '1px solid #ddd',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const btnPrimary: React.CSSProperties = { background: '#29b6f6', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' };
const btnConfirm: React.CSSProperties = { display: 'block', width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '16px 18px', fontWeight: 800, fontSize: 17, cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.45)' };
const btnDisabled: React.CSSProperties = { background: '#ccc', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, cursor: 'not-allowed' };
const btnGhost: React.CSSProperties = { background: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 };

export default MugDesignView;
